import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { Router } from '@abyss/web/ui/Router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { CUSTOM_EVENTS, Constants, DDP_Code } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import GuestFooter from '../../common/GuestFooter/GuestFooter';
import GuestHeader from '../../common/GuestHeader/GuestHeader';
import { LawwSignInAlert } from '../../common/GuestHeader/LawwSignInAlert';
import { usePortalSignInData } from '../../common/GuestHeader/utils';
import { LoadingSpinnerComp } from '../../common/Loader';
import { PortalData } from '../../common/Portal';
import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { parseLOB } from '../../utils/user.utils';
import { getLanguage } from '../ProviderSearch/context/Internationalization/helpers';
import { LANGUAGES } from '../ProviderSearch/context/Internationalization/types';
import { COVERAGE_TYPE_HOME_PATH_MAP } from './constants';
import {
  PSXBlueGreenCanaryGraphqlApi,
  PSXBlueGreenCanaryParcelUrl,
  getClientPortalConfig,
  getSessionPortalState,
  isLawwGuest,
  populatePortalDataFromGuestPlanPicker,
  requestPortalStateFromSession,
  setSessionPortalState,
} from './utils';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'abyss-parcel': any;
      'portal-data': any;
    }
  }
}

export const ProviderSearchForPortalContainer = () => {
  const { state } = useLocation();
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const flags = useFlags();
  const { shouldShowSignIn } = usePortalSignInData();
  const { portalData: parentPortalData } = useContext(PortalContext);

  // Get lagoon config for ENI NICE plan category
  const allConfigs = useLagoon('config');
  const eniNicePlanCategoryName = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.ENI_NICE_PLAN_CATEGORY_NAME
  );
  const {
    isNavigatingMaestro,
    requestData,
    isNavigatingFromJarvis,
    setResponseData,
  } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
      isNavigatingFromJarvis: state.isNavigatingFromJarvis,
      requestData: state.requestData,
      setResponseData: state.setResponseData,
    }))
  );

  // Sync maestroResponseData from the inner parcel bundle into this (outer)
  // store so that GuestHeader — which subscribes to the outer store — re-renders
  // whenever the user adds, updates, or removes a provider inside the parcel.
  useEffect(() => {
    const handleMaestroResponseUpdate = (event: CustomEvent) => {
      setResponseData(event.detail);
    };
    window.addEventListener(
      CUSTOM_EVENTS.RESPONSE_DATA_UPDATED,
      handleMaestroResponseUpdate as EventListener
    );
    return () => {
      window.removeEventListener(
        CUSTOM_EVENTS.RESPONSE_DATA_UPDATED,
        handleMaestroResponseUpdate as EventListener
      );
    };
  }, [setResponseData]);

  const [syncedState, setSyncedState] = useState<Record<
    string,
    unknown
  > | null>(getSessionPortalState<Record<string, unknown>>());
  const [isPortalStateSyncPending, setIsPortalStateSyncPending] =
    useState<boolean>(!(state || syncedState));

  const hasPortalState = Boolean(state || syncedState);
  const savedState = state || syncedState || {};
  const urlParams = new URLSearchParams(window.location.search);
  // supports both 'locale' and 'lang' params for backward compatibility
  const lang =
    parentPortalData?.portalConfig?.language ||
    getLanguage(urlParams.get('locale') || urlParams.get('lang') || '')
      ?.locale ||
    LANGUAGES.en.locale;
  const {
    lineOfBusiness,
    reciprocityId,
    population,
    coverageTypeCode,
    coverageEffStartDate,
    coverageEffEndDate,
    stateCode,
    signInUrl,
    searchDirectory,
    planCategory,
    guestPlanIdentifier,
    rulesPackageKey,
    policyNumber,
    pvrcode,
  } = savedState;

  const normalizedLob = parseLOB(lineOfBusiness);

  const [isDdpEnabled] = useFeatureFlag(
    normalizedLob
      ? [`${ConstantsLagoon.FEATURE_FLAGS.ENABLE_DDP_LOB}${normalizedLob}`]
      : []
  );

  useEffect(() => {
    if (state) {
      setSessionPortalState(state);
      setSyncedState(state);
    }
  }, [state]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (state || syncedState) {
      setIsPortalStateSyncPending(false);
    } else {
      setIsPortalStateSyncPending(true);
      cleanup = requestPortalStateFromSession(setSyncedState);

      timeoutId = setTimeout(() => {
        setIsPortalStateSyncPending(false);
      }, 1200);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [state, syncedState]);

  const homePath = COVERAGE_TYPE_HOME_PATH_MAP[coverageTypeCode] || '/';

  const clientName =
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.CLIENT_NAME) ||
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.PORTAL_NAME);

  const clientPortalConfig = getClientPortalConfig(clientName);

  // Derive the inner parcel's basePath from the parent portal's basePath so the
  // React Router basename matches the actual browser URL regardless of the portal's mount path.
  const outerBasePath = parentPortalData?.portalConfig?.basePath || '';
  const innerBasePath = outerBasePath
    ? `${outerBasePath}/browse`
    : '/guest-plan-selection/browse';

  const portalData: PortalData = {
    portalName: clientPortalConfig.portalName,
    ...(clientPortalConfig.portalIdentifier && {
      portalIdentifier: clientPortalConfig.portalIdentifier,
    }),
    portalConfig: {
      basePath: innerBasePath,
      headerChipsEnabled: coverageTypeCode === 'M',
      ...(clientPortalConfig.themeVersion && {
        themeVersion: clientPortalConfig.themeVersion,
      }),
      ...(clientPortalConfig.themeOverride && {
        themeOverride: clientPortalConfig.themeOverride,
      }),
      theme: clientPortalConfig.theme,
      language: lang,
      homePage: homePath,
      isGuestUser: true,
      showCost: false,
      showTreatmentCenter: true,
      includeBaseCss: true,
      showVirtualVisits: true,
      showOnlineScheduling: shouldShowSignIn,
      graphQlUrl:
        parentPortalData?.portalConfig?.graphQlUrl ||
        PSXBlueGreenCanaryGraphqlApi(),
      signInUrl,
      mapLocationUrl: parentPortalData?.portalConfig?.mapLocationUrl || '/api',
      ...(isLawwGuest() && { reciprocityId, showEapFilter: true }),
    },
    portalMemberInfo: populatePortalDataFromGuestPlanPicker(
      lineOfBusiness,
      reciprocityId,
      population,
      coverageTypeCode,
      coverageEffStartDate,
      coverageEffEndDate,
      stateCode,
      state?.planName ?? savedState?.planName,
      state?.planYear ?? savedState?.planYear,
      searchDirectory,
      planCategory,
      guestPlanIdentifier,
      isDdpEnabled ? DDP_Code.All : undefined,
      rulesPackageKey,
      policyNumber,
      pvrcode,
      eniNicePlanCategoryName
    ),
    isNavigatingMaestro,
    requestData,
    isNavigatingFromJarvis,
  };

  const parcelUrl =
    parentPortalData?.portalConfig?.psxParcelUrl ||
    PSXBlueGreenCanaryParcelUrl(flags, searchParams) ||
    config('PSX_PARCEL_URL') ||
    '';

  const [isParcelReady, setIsParcelReady] = useState(
    () => customElements.get('abyss-parcel') !== undefined
  );

  useEffect(() => {
    if (!parcelUrl) return;
    customElements
      .whenDefined('abyss-parcel')
      .then(() => setIsParcelReady(true));
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = parcelUrl;
    document.head.appendChild(script);
  }, [parcelUrl]);

  // Avoid booting the parcel with incomplete portal data, which caused details to never load.
  if (!hasPortalState && isPortalStateSyncPending) {
    return null;
  }

  if (!isParcelReady) {
    return <LoadingSpinnerComp isLoading={true} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {!isLawwGuest() && <GuestHeader />}
      {isLawwGuest() && <LawwSignInAlert />}
      <Router.MetaTags title="Find Care" />
      <abyss-parcel
        import="provider-search-for-portal"
        portal-data={JSON.stringify(portalData)}
      />
      {!isLawwGuest() && <GuestFooter />}
    </div>
  );
};
